import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  CheckCircle2,
  Calendar,
  Clock,
  Users,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

const Button = /** @type {React.ComponentType<any>} */ (RawButton);
const Card = /** @type {React.ComponentType<any>} */ (RawCard);

/**
 * @typedef {Object} Course
 * @property {string} image
 * @property {string} name
 * @property {string} city
 * @property {string} country
 *
 * @typedef {Object} Confirmation
 * @property {Course} course
 * @property {Date} date
 * @property {string} teeTime
 * @property {number} players
 * @property {number} totalAmount
 *
 * @typedef {Object} ConfirmationViewProps
 * @property {Confirmation} confirmation
 * @property {() => void} onBackToSearch
 * @property {() => void} onProceedToFerry
 * @property {() => void} onCompleteBooking
 * @property {boolean} showNextStep
 */

/**
 * @param {ConfirmationViewProps} props
 */
export default function ConfirmationView({
  confirmation,
  onBackToSearch,
  onProceedToFerry,
  onCompleteBooking,
  showNextStep,
}) {
  const { course, date, teeTime, players, totalAmount } = confirmation;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <div className="p-8 sm:p-12 text-center bg-white border border-gray-100 rounded-2xl shadow-md">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#0B2421]"
          >
            <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-[#0B2421]">
              Golf Selection Confirmed!
            </h1>
            <p className="text-lg mb-8 text-gray-500">
              Your tee time has been reserved
            </p>
          </motion.div>

          {/* Booking Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-left space-y-4 mb-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src={course.image}
                  alt={course.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[#0B2421]">
                  {course.name}
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                  <MapPin className="h-3 w-3" />
                  <span>{course.city}, {course.country}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0B2421]/8">
                  <Calendar className="h-5 w-5 text-[#0B2421]" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Date</div>
                  <div className="font-semibold text-[#0B2421]">
                    {format(date, "dd MMM yyyy")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0B2421]/8">
                  <Clock className="h-5 w-5 text-[#0B2421]" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Tee Time</div>
                  <div className="font-semibold text-[#0B2421]">{teeTime}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0B2421]/8">
                  <Users className="h-5 w-5 text-[#0B2421]" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Players</div>
                  <div className="font-semibold text-[#0B2421]">{players}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0B2421]/8">
                  <div className="text-xs font-bold text-[#D4AF37]">SGD</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Subtotal</div>
                  <div className="font-semibold text-[#D4AF37]">
                    SGD {totalAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="space-y-3"
          >
            <Button
              onClick={onCompleteBooking}
              className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
            >
              Complete Booking Now
            </Button>
            {showNextStep && (
              <Button
                onClick={onProceedToFerry}
                className="w-full h-14 font-semibold text-lg bg-gray-100 text-[#0B2421] hover:bg-gray-200"
              >
                Continue to Ferry Booking
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onBackToSearch}
              className="w-full h-10 text-gray-500 hover:text-[#0B2421]"
            >
              Change Golf Selection
            </Button>
          </motion.div>

          {/* Info Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <p className="text-sm text-amber-700">
              💡 Complete now or add more services like ferry and land transport
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
